import {
  BillingOperationResult,
  ChartDataResponse,
  ReadingsDataProcessedResponse,
  useAsyncOperationWithResult,
  useChartData,
  useSiteHealthEvents,
  useSiteOperationalActions,
} from "@inrange/building-manager-api-client";
import { SiteAllowStringValues } from "@inrange/building-manager-api-client/models-site";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import Select from "react-select";
import { DateRangePicker } from "../../../components";
import Loading from "../../Loading";
import DbRecordsWritten from "../siteSections/DbRecordsWritten";
import { HealthEventsTable } from "../SiteHealthStatus";
import ChartProcessed from "./ChartProcessed";
import DataGapHandling from "./DataGapHandling";
import RecentDataTableProcessed from "./RecentDataTableProcessed";
import SourceTypeCounts from "./SourceTypeCounts";
import { AggregationMode, Formatters, PresetName } from "./types";

interface SiteReadingsProcessedProps {
  site: SiteAllowStringValues;
  siteId: string;
  selectedEsysIdx: string;
  selectedEsysUuid: string | undefined;
  formatters: Formatters;
  initialStartDate: string;
  initialEndDate: string;
  urlParamsConsumed: boolean;
  onUrlParamsConsumed: () => void;
  onDateChange: (startDate: string, endDate: string) => void;
  aggregationMode: AggregationMode;
  onAggregationChange: (mode: AggregationMode) => void;
  preset: PresetName;
  onPresetChange: (preset: PresetName) => void;
}

const SiteReadingsProcessed = ({
  site,
  siteId,
  selectedEsysIdx,
  selectedEsysUuid,
  formatters,
  initialStartDate,
  initialEndDate,
  urlParamsConsumed,
  onUrlParamsConsumed,
  onDateChange,
  aggregationMode,
  onAggregationChange,
  preset,
  onPresetChange,
}: SiteReadingsProcessedProps) => {
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);
  const [timePeriod, setTimePeriod] = useState<string>("");
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { processBilling } = useSiteOperationalActions();
  const { healthEvents } = useSiteHealthEvents(siteId);

  const visibleChartEvents = useMemo(() => {
    const allChartEvents =
      healthEvents.data?.events.filter(
        (e) =>
          (e.impactType === "unplanned_pv_outage" ||
            e.impactType === "unplanned_battery_outage" ||
            e.category === "event_log") &&
          e.checkType !== "zero_export_violation"
      ) ?? [];
    if (!startDate || !endDate) return allChartEvents;
    const rangeStart = new Date(startDate).getTime();
    const rangeEnd = new Date(endDate + "T23:59:59").getTime();
    return allChartEvents
      .filter((e) => {
        const detectedMs = new Date(e.detectedAt).getTime();
        const resolvedMs = e.resolvedAt
          ? new Date(e.resolvedAt).getTime()
          : Infinity;
        if (detectedMs >= rangeEnd || resolvedMs <= rangeStart) return false;
        // Filter by esys when one is selected (site-level events always show)
        if (selectedEsysUuid && e.esysUuid && e.esysUuid !== selectedEsysUuid)
          return false;
        return true;
      })
      .sort(
        (a, b) =>
          new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
      );
  }, [healthEvents.data, startDate, endDate, selectedEsysUuid]);

  const onSettled = useCallback(() => {
    setIsSubmitting(false);
  }, []);

  const { setOperationKey, result, setResult } =
    useAsyncOperationWithResult<BillingOperationResult>({
      siteId,
      onSettled,
    });

  const runProcessReadings = async () => {
    setResult(null);
    setIsSubmitting(true);
    try {
      const response = await processBilling.mutateAsync({
        siteId,
        readingAction: "current_month",
        readingDbInsert: true,
        readingDbUpdate: true,
        attachReadings: false,
      });
      setOperationKey(response.operationKey);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      setResult({ type: "error", message: errorMessage });
      setIsSubmitting(false);
    }
  };

  const esysEntries = useMemo(
    () => Object.entries(site.esys) as [string, any][],
    [site.esys]
  );

  // Forecast data is always loaded (no toggle)

  const hasBattery =
    site?.totalBatterySummary !== undefined &&
    site?.totalBatterySummary !== null;

  const isImportOnly = site.totalInstalledCapacity === 0;
  const isDemo = site.operationalStatus === "operationalDemo";

  // Fetch processed readings data
  const processedDataQuery = useChartData({
    dataType: "readings_data_processed",
    options: {},
    siteId: siteId,
    enabled: true,
  });

  // Determine esysIdx for forecast query - use "all" for whole-site view
  const forecastEsysIdx = useMemo(() => {
    if (selectedEsysIdx) return selectedEsysIdx;
    return "all";
  }, [selectedEsysIdx]);

  const forecastP50Query = useChartData({
    dataType: "energy_flows",
    siteId: siteId,
    options: { esysIdx: forecastEsysIdx, pvForecastProbability: "P50" },
    enabled: true,
  });

  const forecastP90Query = useChartData({
    dataType: "energy_flows",
    siteId: siteId,
    options: { esysIdx: forecastEsysIdx, pvForecastProbability: "P90" },
    enabled: true,
  });

  const isLoading = processedDataQuery.isLoading;
  const queryError = processedDataQuery.error;
  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : "Unknown error"
    : null;

  // Calculate available time periods from the data
  const availableTimePeriods = useMemo(() => {
    const periods: string[] = [];
    periods.push("all");

    let data: { x: string | number }[] = [];
    if (processedDataQuery.data) {
      const allData = (processedDataQuery.data as ReadingsDataProcessedResponse)
        .data;
      data = selectedEsysUuid
        ? allData.filter((point) => point.esysUuid === selectedEsysUuid)
        : allData;
    }

    if (data.length > 0) {
      const yearMonths = new Map<string, Set<string>>();

      data.forEach((point) => {
        const dateStr = formatters.toDateString(point.x);
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(0, 7);

        if (!yearMonths.has(year)) {
          yearMonths.set(year, new Set());
        }
        yearMonths.get(year)!.add(month);
      });

      const sortedYears = Array.from(yearMonths.keys()).sort().reverse();
      sortedYears.forEach((year) => {
        periods.push(year);
        const months = Array.from(yearMonths.get(year)!).sort().reverse();
        months.forEach((month) => periods.push(month));
      });

      periods.push("custom");
    }

    return periods;
  }, [processedDataQuery.data, selectedEsysUuid, formatters]);

  // Extract unique source values from the data within the selected date range
  const availableSources = useMemo(() => {
    const sources = new Set<string>();

    if (processedDataQuery.data) {
      const allData = (processedDataQuery.data as ReadingsDataProcessedResponse)
        .data;

      const { startMs, endMs } =
        startDate && endDate
          ? formatters.dateRangeToTimestamps(startDate, endDate)
          : { startMs: 0, endMs: Infinity };

      const data = allData.filter((point) => {
        if (selectedEsysUuid && point.esysUuid !== selectedEsysUuid)
          return false;
        if (startDate && endDate) {
          const pointMs = new Date(point.x).getTime();
          if (pointMs < startMs || pointMs > endMs) return false;
        }
        return true;
      });

      data.forEach((point) => {
        if (point.generationSource) sources.add(point.generationSource);
        if (point.importSource) sources.add(point.importSource);
        if (point.exportSource) sources.add(point.exportSource);
        if (hasBattery) {
          if (point.batteryFinalChargeSource)
            sources.add(point.batteryFinalChargeSource);
        }
      });
    }

    return Array.from(sources).sort();
  }, [
    processedDataQuery.data,
    hasBattery,
    selectedEsysUuid,
    startDate,
    endDate,
    formatters,
  ]);

  // Remove selected sources that are no longer available (e.g. after date range change)
  useEffect(() => {
    if (selectedSources.length > 0) {
      const stillValid = selectedSources.filter((s) =>
        availableSources.includes(s)
      );
      if (stillValid.length !== selectedSources.length) {
        setSelectedSources(stillValid);
      }
    }
  }, [availableSources, selectedSources]);

  // Helper function to find matching time period for given dates
  const findMatchingPeriod = (
    newStartDate: string,
    newEndDate: string,
    periods: string[]
  ): string | undefined => {
    return periods.find((period) => {
      if (period === "all" || period === "custom") return false;

      if (period.length === 4) {
        return (
          newStartDate === `${period}-01-01` && newEndDate === `${period}-12-31`
        );
      } else if (period.length === 7) {
        const [year, month] = period.split("-");
        const monthNum = parseInt(month, 10);
        const lastDay = new Date(parseInt(year), monthNum, 0).getDate();
        return (
          newStartDate === `${period}-01` &&
          newEndDate === `${period}-${lastDay.toString().padStart(2, "0")}`
        );
      }
      return false;
    });
  };

  // Initialize with default period when data is available
  useEffect(() => {
    if (processedDataQuery.data && !isInitialized) {
      // If dates were provided in URL and not yet consumed, use them
      if (initialStartDate && initialEndDate && !urlParamsConsumed) {
        setStartDate(initialStartDate);
        setEndDate(initialEndDate);
        const matchingPeriod = findMatchingPeriod(
          initialStartDate,
          initialEndDate,
          availableTimePeriods
        );
        setTimePeriod(matchingPeriod || "custom");
        setIsInitialized(true);
        onUrlParamsConsumed();
        return;
      }

      // Set to latest full month for processed data
      const allData = (processedDataQuery.data as ReadingsDataProcessedResponse)
        .data;
      const data = selectedEsysUuid
        ? allData.filter((point) => point.esysUuid === selectedEsysUuid)
        : allData;

      if (data.length > 0) {
        let maxTime = new Date(data[0].x).getTime();
        for (const point of data) {
          const time = new Date(point.x).getTime();
          if (time > maxTime) maxTime = time;
        }
        const latestDate = new Date(maxTime);
        const latestMonth = latestDate.toISOString().substring(0, 7);
        setTimePeriod(latestMonth);

        const [year, month] = latestMonth.split("-");
        const monthNum = parseInt(month, 10);
        const lastDay = new Date(parseInt(year), monthNum, 0).getDate();
        const newStartDate = `${latestMonth}-01`;
        const newEndDate = `${latestMonth}-${lastDay.toString().padStart(2, "0")}`;
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        onDateChange(newStartDate, newEndDate);
        setIsInitialized(true);
      }
    }
  }, [
    processedDataQuery.data,
    isInitialized,
    selectedEsysUuid,
    initialStartDate,
    initialEndDate,
    availableTimePeriods,
    urlParamsConsumed,
    onUrlParamsConsumed,
    onDateChange,
  ]);

  // Handle time period selection
  const handleTimePeriodChange = (period: string) => {
    setTimePeriod(period);

    if (period === "all") {
      // Set explicit date range from earliest to latest data point
      const allData = (processedDataQuery.data as ReadingsDataProcessedResponse)
        ?.data;
      const data = selectedEsysUuid
        ? allData?.filter((point) => point.esysUuid === selectedEsysUuid)
        : allData;
      if (data && data.length > 0) {
        let minTime = new Date(data[0].x).getTime();
        let maxTime = minTime;
        for (const point of data) {
          const t = new Date(point.x).getTime();
          if (t < minTime) minTime = t;
          if (t > maxTime) maxTime = t;
        }
        const newStartDate = new Date(minTime).toISOString().split("T")[0];
        const newEndDate = new Date(maxTime).toISOString().split("T")[0];
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        onDateChange(newStartDate, newEndDate);
      } else {
        setStartDate("");
        setEndDate("");
        onDateChange("", "");
      }
    } else if (period === "custom") {
      // Keep existing dates
    } else if (period.includes("-") && period.length === 7) {
      const [year, month] = period.split("-");
      const monthNum = parseInt(month, 10);
      const lastDay = new Date(parseInt(year), monthNum, 0).getDate();
      const newStartDate = `${period}-01`;
      const newEndDate = `${period}-${lastDay.toString().padStart(2, "0")}`;
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      onDateChange(newStartDate, newEndDate);
    } else {
      const newStartDate = `${period}-01-01`;
      const newEndDate = `${period}-12-31`;
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      onDateChange(newStartDate, newEndDate);
    }
  };

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    onDateChange(newStartDate, newEndDate);

    const matchingPeriod = findMatchingPeriod(
      newStartDate,
      newEndDate,
      availableTimePeriods
    );
    setTimePeriod(matchingPeriod || "custom");
  };

  const hasData =
    processedDataQuery.data &&
    (processedDataQuery.data as ReadingsDataProcessedResponse).data.length > 0;

  return (
    <>
      <Row className="mb-3 gy-3">
        <Col sm={3}>
          <Form.Group>
            <Form.Label>Time Period</Form.Label>
            <Form.Select
              value={timePeriod}
              onChange={(e) => handleTimePeriodChange(e.target.value)}
            >
              {availableTimePeriods.map((period) => {
                let label = period;
                if (period === "all") {
                  label = "All available";
                } else if (period === "custom") {
                  label = "Custom";
                }
                return (
                  <option key={period} value={period}>
                    {label}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col sm={5}>
          <Form.Group>
            <Form.Label>Date Range</Form.Label>
            <DateRangePicker
              value={{
                start: startDate ? new Date(startDate) : null,
                end: endDate ? new Date(endDate) : null,
              }}
              onChange={({ start, end }) => {
                const newStart = start ? start.toISOString().split("T")[0] : "";
                const newEnd = end ? end.toISOString().split("T")[0] : "";
                handleDateChange(newStart, newEnd);
              }}
              mode="date"
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3 gy-3">
        <Col sm={2}>
          <Form.Group>
            <Form.Label>Aggregation</Form.Label>
            <Form.Select
              value={aggregationMode}
              onChange={(e) =>
                onAggregationChange(e.target.value as AggregationMode)
              }
            >
              <option value="none">None</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </Form.Select>
          </Form.Group>
        </Col>

      </Row>

      {availableSources.length > 0 && (
        <Row className="mb-3">
          <Col sm={3}>
            <Form.Group>
              <Form.Label>Filter to source</Form.Label>
              <Select
                placeholder="Filter by source..."
                value={selectedSources.map((s) => ({ label: s, value: s }))}
                onChange={(values) => {
                  setSelectedSources(
                    values ? values.map((value) => value.value) : []
                  );
                }}
                options={availableSources.map((source) => ({
                  label: source,
                  value: source,
                }))}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border:
                      "var(--bs-border-width) solid var(--bs-border-color)",
                    borderRadius: "var(--bs-border-radius)",
                    color: "var(--bs-body-color)",
                    minWidth: "250px",
                  }),
                }}
                isMulti
                isClearable
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      {!isImportOnly && !isDemo && (
        <Row className="mb-3">
          <Col sm="auto" className="d-flex align-items-center gap-2">
            <Button
              variant="primary"
              onClick={runProcessReadings}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Processing...
                </>
              ) : (
                "Load latest processed readings"
              )}
            </Button>
          </Col>
        </Row>
      )}

      {result && (
        <Alert
          variant={result.type === "success" ? "success" : "danger"}
          dismissible
          onClose={() => setResult(null)}
          className="mb-3"
        >
          {result.type === "success" && result.response ? (
            <>
              <div>{result.message}</div>
              {result.response.reading_db_update_stats &&
                Object.keys(result.response.reading_db_update_stats).length >
                  0 && (
                  <DbRecordsWritten
                    dbRecordsWritten={result.response.reading_db_update_stats}
                    esys={site.esys}
                  />
                )}
            </>
          ) : (
            result.message
          )}
        </Alert>
      )}

      {isLoading && <Loading label="Loading readings data..." height="60vh" />}

      {error && (
        <Row>
          <Col>
            <div className="alert alert-danger" role="alert">
              Error loading readings data: {error}
            </div>
          </Col>
        </Row>
      )}

      {!isLoading && !error && !hasData && (
        <Row>
          <Col>
            <div className="alert alert-info" role="alert">
              No readings data available for this site
            </div>
          </Col>
        </Row>
      )}

      {!isLoading && !error && hasData && isInitialized && (
        <ChartProcessed
          data={processedDataQuery.data as ReadingsDataProcessedResponse}
          selectedEsysUuid={selectedEsysUuid}
          startDate={startDate}
          endDate={endDate}
          selectedSources={selectedSources}
          hasBattery={hasBattery}
          hasAnyZeroExport={site?.hasAnyZeroExport ?? false}
          isImportOnly={isImportOnly}
          formatters={formatters}
          aggregationMode={aggregationMode}
          forecastP50Data={
            forecastP50Query.data
              ? (forecastP50Query.data as ChartDataResponse).data
              : undefined
          }
          forecastP90Data={
            forecastP90Query.data
              ? (forecastP90Query.data as ChartDataResponse).data
              : undefined
          }
          weatherActualsData={
            processedDataQuery.data
              ? (processedDataQuery.data as ReadingsDataProcessedResponse).weatherActuals
              : undefined
          }
          curtailmentData={
            processedDataQuery.data
              ? (processedDataQuery.data as ReadingsDataProcessedResponse).curtailment
              : undefined
          }
          expectedGenerationPeriods={
            forecastP50Query.data
              ? (forecastP50Query.data as ChartDataResponse).expectedGenerationPeriods
              : undefined
          }
          eventLogEvents={visibleChartEvents}
          esysEntries={esysEntries}
          siteName={site?.name as string | undefined}
          preset={preset}
          onPresetChange={onPresetChange}
          forecastLoading={forecastP50Query.isLoading || forecastP90Query.isLoading}
        />
      )}

      {!isLoading && visibleChartEvents.length > 0 && (
        <Row className="mt-3">
          <Col>
            <HealthEventsTable
              events={visibleChartEvents}
              siteId={siteId}
              timeZone={site.timeZone as string}
              esysEntries={esysEntries}
              title={
                hasBattery
                  ? "PV Availability / Battery Availability / Health Events"
                  : "PV Availability / Health Events"
              }
              showDateFilter={false}
              defaultStatusFilter="all"
              showDeleteColumn={false}
              readOnly
            />
          </Col>
        </Row>
      )}

      {!isLoading && !isDemo && (
        <DataGapHandling
          siteId={siteId}
          selectedEsysUuid={selectedEsysUuid}
          startDate={startDate}
          endDate={endDate}
          measurementType={site.measurementType as string}
        />
      )}

      {!isLoading && (
        <SourceTypeCounts
          dataMode="processed"
          processedData={
            processedDataQuery.data as ReadingsDataProcessedResponse
          }
          rawData={undefined}
          curtailmentData={
            processedDataQuery.data
              ? (processedDataQuery.data as ReadingsDataProcessedResponse).curtailment
              : undefined
          }
          hasBattery={hasBattery}
          selectedEsysUuid={selectedEsysUuid}
          startDate={startDate}
          endDate={endDate}
          formatters={formatters}
        />
      )}

      {!isLoading && processedDataQuery.data && (
        <RecentDataTableProcessed
          site={site}
          data={processedDataQuery.data as ReadingsDataProcessedResponse}
          curtailmentData={
            (processedDataQuery.data as ReadingsDataProcessedResponse).curtailment
          }
          selectedEsysUuid={selectedEsysUuid}
          startDate={startDate}
          endDate={endDate}
          isImportOnly={isImportOnly}
          formatters={formatters}
        />
      )}
    </>
  );
};

export default SiteReadingsProcessed;
